/**
 * StackingRow — a flex row that becomes a column at large OS text sizes.
 *
 * Use it for label/value rows and two-button rows that cannot fit side by
 * side once text is 1.3x or larger. At normal sizes it renders exactly the
 * row the caller's `style` describes; at large text it switches to a
 * stretched column and applies `stackedStyle` on top (e.g. a tighter gap).
 *
 * @example
 * <StackingRow style={styles.detailRow} stackedStyle={styles.detailRowStacked}>
 *   <Text>Fee</Text>
 *   <Text>$2.50</Text>
 * </StackingRow>
 */
import React, { createContext, useContext } from 'react';
import { StyleSheet, View, type StyleProp, type ViewProps, type ViewStyle } from 'react-native';

import { useFontScale } from '@/hooks/shared/useFontScale';

/**
 * True inside a StackingRow that has stacked into a column. Children whose
 * row-mode style assumes a horizontal main axis (a `flex: 1` button wrapper)
 * read it to switch to a column-safe style.
 */
const StackingRowContext = createContext(false);
export const useStackedRow = (): boolean => useContext(StackingRowContext);

export type StackingRowProps = ViewProps & {
  /** Applied after `style`, only when the row has stacked into a column. */
  stackedStyle?: StyleProp<ViewStyle>;
};

export const StackingRow: React.FC<StackingRowProps> = ({ style, stackedStyle, ...rest }) => {
  const { isLargeText } = useFontScale();
  return (
    <StackingRowContext.Provider value={isLargeText}>
      <View
        style={[styles.row, style, isLargeText && styles.stacked, isLargeText && stackedStyle]}
        {...rest}
      />
    </StackingRowContext.Provider>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
  },
  stacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
});
